import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";

import useLiveActions from "./useLiveActions";
import useLiveClock from "./useLiveClock";
import useLiveQueries, { LIVE_DETAILS_INTERVAL_MS } from "./useLiveQueries";
import ContentContext from "../../contexts/ContentContext";
import {
  canExtendLive,
  getLiveQuotaSuggestions,
  getLiveQuotaSummary,
  isValidLiveDuration,
} from "../../utils/liveQuota";
import { readLiveSettings } from "../../utils/liveSettings";
import {
  getLiveView,
  getServerStatus,
  isTerminalStatus,
  LIVE_FINALIZE_DELAY_MS,
  parseUtcMs,
} from "../../utils/liveView";

import type {
  PendingState,
  PollingFlags,
  ReplayRangeMs,
  UseLiveRegieResult,
} from "./live.types";
import type { GetMediaResponse } from "../../api/MediaAPI.types";
import type { LiveStreamStatus } from "../../models/LiveStream";
import type { AppState } from "../../reducers";
import type { LiveQuotaArgs, LiveQuotaSuggestion } from "../../utils/liveQuota";
import type { LiveView } from "../../utils/liveView";
import type { ContentContextValues } from "../content/ContentTabs";

const LIVE_MEDIA_INTERVAL_MS = 30_000;
const LIVE_SCHEDULED_WINDOW_MS = 10 * 60 * 1000;
const LIVE_ACTION_POLL_INTERVAL_MS = 5000;
// Without server confirmation after this delay, a stop/publish stops pending.
const LIVE_ACTION_CONFIRM_TIMEOUT_MS = 30_000;
const CLOCK_INTERVAL_MS = 1000;
const CLOCK_SLOW_INTERVAL_MS = 15_000;

const NO_SUGGESTIONS: LiveQuotaSuggestion[] = [];

const IDLE_FLAGS: PollingFlags = {
  details: false,
  stats: false,
  plan: false,
  mediaIntervalMs: false,
};

function isPreparationStatus(status: LiveStreamStatus): boolean {
  return status === "none" || status === "scheduled";
}

function buildQuotaArgs(
  durationSeconds: number,
  quality: string,
  audioOnly: boolean,
  encodingPlan: LiveQuotaArgs["encodingPlan"] | undefined,
  liveEncodingPlan: string,
): LiveQuotaArgs | null {
  if (!encodingPlan || !liveEncodingPlan) return null;
  return {
    durationSeconds,
    quality,
    audioOnly,
    encodingPlan,
    liveEncodingPlan,
  };
}

function sameFlags(a: PollingFlags, b: PollingFlags): boolean {
  return (
    a.details === b.details &&
    a.stats === b.stats &&
    a.plan === b.plan &&
    a.mediaIntervalMs === b.mediaIntervalMs
  );
}

function getMediaIntervalMs(
  view: LiveView,
  pending: PendingState | null,
  scheduledSoon: boolean,
): number | false {
  // Stats may fail or the server may never reflect the action: the media is
  // the fallback source that lets the pending action be confirmed.
  if (pending?.action === "stop" || pending?.action === "publish") {
    return LIVE_ACTION_POLL_INTERVAL_MS;
  }
  // While the creation is not reflected by the media, the media is the only
  // source of the server status (stats are not polled yet): follow the details pace.
  if (view.state === "provisioning") return LIVE_DETAILS_INTERVAL_MS;
  if (scheduledSoon) return LIVE_MEDIA_INTERVAL_MS;
  return false;
}

function getClockIntervalMs(view: LiveView): number | null {
  if (view.state === "provisioning" || view.state === "stopping") {
    return CLOCK_INTERVAL_MS;
  }
  if (view.phase === "antenne") return CLOCK_SLOW_INTERVAL_MS;
  return null;
}

function getNextWakeAt(
  now: number,
  clockIntervalMs: number | null,
  view: LiveView,
  pending: PendingState | null,
): number | null {
  const scheduledStartMs = parseUtcMs(view.scheduledStart);
  const windowStartMs =
    scheduledStartMs === null
      ? null
      : scheduledStartMs - LIVE_SCHEDULED_WINDOW_MS;
  const isActionPending =
    pending?.action === "stop" || pending?.action === "publish";
  const wakeAtList = [
    clockIntervalMs === null ? null : now + clockIntervalMs,
    windowStartMs !== null && windowStartMs > now ? windowStartMs : null,
    pending && isActionPending
      ? pending.since + LIVE_ACTION_CONFIRM_TIMEOUT_MS
      : null,
  ].filter((wakeAt): wakeAt is number => wakeAt !== null);
  return wakeAtList.length > 0 ? Math.min(...wakeAtList) : null;
}

function getPollingFlags(
  view: LiveView,
  now: number,
  pending: PendingState | null,
  isCreating: boolean,
): PollingFlags {
  const { phase, state } = view;
  const scheduledStartMs = parseUtcMs(view.scheduledStart);
  const scheduledSoon =
    phase === "preparation" &&
    scheduledStartMs !== null &&
    scheduledStartMs - now <= LIVE_SCHEDULED_WINDOW_MS;

  return {
    // Not before createLive has answered: the details do not exist yet.
    details:
      state === "provisioning" &&
      !isCreating &&
      !(view.streamUrl && view.streamKey),
    stats:
      (phase === "connexion" && state !== "provisioning") ||
      phase === "antenne" ||
      state === "stopping",
    // Also needed on air: the extension check reads the quota.
    plan: phase === "preparation" || phase === "antenne",
    mediaIntervalMs: getMediaIntervalMs(view, pending, scheduledSoon),
  };
}

export default function useLiveRegie(): UseLiveRegieResult {
  const { item, isSaved, onItemUpdate, onSave } =
    useContext<ContentContextValues>(ContentContext);

  const options = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config?.data?.organization_options,
  );
  const allowReplay = Boolean(options?.allow_replay);
  const allowLiveOkast = Boolean(options?.allow_live_okast);
  const liveEncodingPlan = options?.live_encoding_plan ?? "";

  const [pending, setPending] = useState<PendingState | null>(null);
  const [terminalSeenAt, setTerminalSeenAt] = useState<number | null>(null);
  const [hasSeenNonTerminal, setHasSeenNonTerminal] = useState(false);
  const [expiredActionSince, setExpiredActionSince] = useState<number | null>(
    null,
  );
  const [lastClockIntervalMs, setLastClockIntervalMs] = useState<number | null>(
    null,
  );
  const [now, setNow] = useState(() => Date.now());
  const [flags, setFlags] = useState<PollingFlags>(IDLE_FLAGS);
  // replay_uuid returned by createReplay, kept until the media reflects it.
  const [replayOverride, setReplayOverride] = useState<string | null>(null);
  // Range (ms) just sent to createReplay/updateReplay, kept until the media
  // manifest reflects it.
  const [replayRangeOverride, setReplayRangeOverride] =
    useState<ReplayRangeMs | null>(null);
  // Plan dataUpdatedAt captured at a createLive quota refusal (null: none).
  const [quotaRefusedAt, setQuotaRefusedAt] = useState<number | null>(null);
  const [scheduledStartSeen, setScheduledStartSeen] = useState<string | null>(
    null,
  );

  const { mediaQuery, detailsQuery, statsQuery, planQuery } = useLiveQueries(
    item.uuid,
    flags,
    quotaRefusedAt,
  );
  // Refused until the plan is refetched after the refusal (server keeps the final say).
  const quotaRefused =
    quotaRefusedAt !== null && planQuery.dataUpdatedAt <= quotaRefusedAt;

  const { refetch: refetchMedia } = mediaQuery;
  const { refetch: refetchDetails } = detailsQuery;
  const { refetch: refetchPlan } = planQuery;
  const mediaData = mediaQuery.data ?? item;
  const mediaManifest = mediaData.live_stream?.manifest;
  const mediaReplayStart = mediaManifest?.replay_start_ms;
  const mediaReplayEnd = mediaManifest?.replay_end_ms;
  // The override stands until the media manifest reflects the same range.
  const useRangeOverride =
    replayRangeOverride !== null &&
    !(
      mediaReplayStart === replayRangeOverride.start &&
      mediaReplayEnd === replayRangeOverride.end
    );
  if (replayRangeOverride !== null && !useRangeOverride) {
    // Render-time update, guarded: converges once the media reflects the range.
    setReplayRangeOverride(null);
  }
  const media = {
    ...mediaData,
    replay_uuid: mediaData.replay_uuid ?? replayOverride,
    live_stream:
      useRangeOverride && mediaData.live_stream
        ? {
            ...mediaData.live_stream,
            manifest: {
              ...mediaData.live_stream.manifest,
              replay_start_ms: replayRangeOverride.start,
              replay_end_ms: replayRangeOverride.end,
            },
          }
        : mediaData.live_stream,
  };
  const stats = statsQuery.data ?? null;
  const details = detailsQuery.data ?? null;
  // Preparation edits live in the item; past it the server is authoritative.
  const itemSettings = readLiveSettings(item);
  const serverSettings = readLiveSettings(mediaData);

  const audioOnly = Boolean(item.audio_only);
  const planData = planQuery.data;
  const { durationSeconds: itemDuration, maxResolution: itemResolution } =
    itemSettings;
  const { quota, suggestions } = useMemo(() => {
    const args = buildQuotaArgs(
      itemDuration,
      itemResolution,
      audioOnly,
      planData,
      liveEncodingPlan,
    );
    return {
      quota: args ? getLiveQuotaSummary(args) : null,
      suggestions: args ? getLiveQuotaSuggestions(args) : NO_SUGGESTIONS,
    };
  }, [itemDuration, itemResolution, audioOnly, planData, liveEncodingPlan]);

  const hasValidSettings =
    isValidLiveDuration(Math.round(itemSettings.durationSeconds / 60)) &&
    Boolean(itemSettings.maxResolution);
  // Unknown quota (plan not loaded) never blocks: the server has the final say.
  const isQuotaAvailable = (quota ? quota.isEnough : true) && !quotaRefused;

  const view = getLiveView({
    media,
    mediaUpdatedAt: mediaQuery.dataUpdatedAt,
    stats,
    statsUpdatedAt: statsQuery.dataUpdatedAt,
    details,
    detailsError: detailsQuery.isError && !detailsQuery.isFetching,
    pending,
    terminalSeenAt,
    now,
    allowReplay,
    isSaved,
    isQuotaAvailable,
    hasValidSettings,
    scheduledStartSeen,
  });
  if (view.scheduledStart && view.scheduledStart !== scheduledStartSeen) {
    setScheduledStartSeen(view.scheduledStart);
  }

  // Polling flags derive from the view, which itself reads the queries:
  // settle them during render so queries get enabled without a lagging pass.
  const settings = view.phase === "preparation" ? itemSettings : serverSettings;
  const { durationSeconds: duration, maxResolution: resolution } = settings;
  const canExtend = useMemo(() => {
    const args = buildQuotaArgs(
      duration,
      resolution,
      audioOnly,
      planData,
      liveEncodingPlan,
    );
    return args ? canExtendLive(args) : false;
  }, [duration, resolution, audioOnly, planData, liveEncodingPlan]);

  const { actions, extendQuotaRefused, isMutating } = useLiveActions({
    item,
    allowReplay,
    canExtend,
    replayUuid: media.replay_uuid,
    setPending,
    setNow,
    setQuotaRefusedAt,
    setReplayOverride,
    setReplayRangeOverride,
    refetchMedia,
    refetchDetails,
    refetchPlan,
    onItemUpdate,
    onSave,
  });

  const nextFlags = getPollingFlags(view, now, pending, isMutating.start);
  if (!sameFlags(flags, nextFlags)) setFlags(nextFlags);

  const serverStatus = getServerStatus({
    media,
    mediaUpdatedAt: mediaQuery.dataUpdatedAt,
    stats,
    statsUpdatedAt: statsQuery.dataUpdatedAt,
  });

  // The server is the source of truth: drop the local action once reflected,
  // and note when a terminal status is first observed (starts finalization).
  // A terminal status already there at page load does not start it.
  const isReflected =
    pending !== null &&
    // Kept until credentials are known (or past connexion) so a retry's
    // re-armed `since` is not lost.
    ((pending.action === "create" &&
      (serverStatus === "live" ||
        isTerminalStatus(serverStatus) ||
        (!isPreparationStatus(serverStatus) &&
          Boolean(view.streamUrl && view.streamKey)))) ||
      (pending.action === "publish" && serverStatus === "live") ||
      (pending.action === "stop" && isTerminalStatus(serverStatus)));
  if (isReflected) setPending(null);

  if (isTerminalStatus(serverStatus)) {
    if (
      terminalSeenAt === null &&
      (pending?.action === "stop" || hasSeenNonTerminal)
    ) {
      const seenAt = Date.now();
      setTerminalSeenAt(seenAt);
      setNow(seenAt);
    }
  } else {
    if (!hasSeenNonTerminal) setHasSeenNonTerminal(true);
    if (terminalSeenAt !== null && pending?.action !== "stop") {
      setTerminalSeenAt(null);
    }
  }

  // A stop/publish the server never reflects must not leave the bar stuck.
  if (
    !isReflected &&
    (pending?.action === "stop" || pending?.action === "publish") &&
    now - pending.since >= LIVE_ACTION_CONFIRM_TIMEOUT_MS
  ) {
    setPending(null);
    setExpiredActionSince(pending.since);
  }

  // Refresh right away when the clock (re)starts: it may have been idle for long.
  const clockIntervalMs = getClockIntervalMs(view);
  if (clockIntervalMs !== lastClockIntervalMs) {
    setLastClockIntervalMs(clockIntervalMs);
    if (clockIntervalMs !== null) setNow(Date.now());
  }

  // Next tick, scheduled live entering its 10-min window, or pending action timeout.
  const nextWakeAt = getNextWakeAt(now, clockIntervalMs, view, pending);
  useLiveClock(nextWakeAt, setNow);

  // One media refetch per phase change, at the end of finalization and on action timeout.
  const finalizedAt =
    terminalSeenAt !== null && now - terminalSeenAt >= LIVE_FINALIZE_DELAY_MS
      ? terminalSeenAt
      : null;
  const mediaRefreshKey = `${view.phase}:${finalizedAt ?? ""}:${expiredActionSince ?? ""}`;
  const lastMediaRefreshKeyRef = useRef(mediaRefreshKey);
  useEffect(() => {
    if (lastMediaRefreshKeyRef.current === mediaRefreshKey) return;
    lastMediaRefreshKeyRef.current = mediaRefreshKey;
    refetchMedia();
  }, [mediaRefreshKey, refetchMedia]);

  // The ONE write outside updateSettings: the backend needs a live_stream
  // object on the item before anything else (as the legacy ContentLive did).
  useEffect(() => {
    if (item.live_stream?.uuid) return;
    const initial = readLiveSettings(item);
    // The backend assigns the uuid and the remaining manifest fields, hence the cast.
    const liveStream = {
      ...item.live_stream,
      status: "none",
      live_start: null,
      live_end: null,
      instant_replay: allowReplay && initial.instantReplay,
      manifest: {
        ...item.live_stream?.manifest,
        max_resolution: initial.maxResolution,
        input_type: initial.inputType,
      },
    } as GetMediaResponse["live_stream"];
    onItemUpdate({ ...item, live_stream: liveStream });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    view,
    settings,
    quota,
    suggestions,
    canExtend,
    extendQuotaRefused,
    allowReplay,
    allowLiveOkast,
    liveEncodingPlan,
    audioOnly,
    isArchived: item.status === "archived",
    isStale: statsQuery.isError && flags.stats,
    now,
    isMutating: { ...isMutating, retryProvisioning: detailsQuery.isFetching },
    actions,
  };
}
